import React, { useState, useContext, useEffect } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import {
	FormControlLabel,
	FormControl,
	InputLabel,
	InputAdornment,
	IconButton,
	Checkbox,
	OutlinedInput,
} from "@mui/material";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { UserContext } from "../../context/UserContext";
import axios from "axios";
import { API_URL } from "../../utils/constants";
import { makeStyles } from "@mui/styles";
import { CircularProgress } from "@mui/material";
import Logo from "../../assets/logo.png";
import { useAlert } from "react-alert";
import PasswordStrengthBar from "react-password-strength-bar";
import ReactCodeInput from "react-code-input";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Fade from "@mui/material/Fade";
import i18n from "../../i18n/config";

function Copyright(props) {
	return (
		<Typography
			variant="body2"
			color="text.secondary"
			align="center"
			{...props}
		>
			{"Copyright © "}
			<Link color="inherit" href="https://applaby.com/">
				Applaby
			</Link>{" "}
			{new Date().getFullYear()}
			{"."}
		</Typography>
	);
}

const useStyles = makeStyles({
	videoContainer: {
		"&:before": {
			content: '""',
			position: "absolute",
			top: 0,
			right: 0,
			left: 0,
			bottom: 0,
			zIndex: 1,
			background:
				"linear-gradient(to right,rgba(0, 0, 0, 0.5),rgba(0, 0, 0, 0.4))",
		},
	},
	link: {
		"&:hover": {
			cursor: "pointer",
			textDecoration: "underline!important",
			textDecorationColor: "#43BD98",
		},
	},
	errorInput: {
		"& .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #eb4d4b",
		},
	},
	passwordStrengthBar: {
		marginTop: 4,
		"& p": {
			fontSize: "12px!important",
		},
	},
	inputCode: {
		minWidth: "288px",
		"& input": {
			"&:focus": {
				outlineColor: "#43BD98!important",
			},
		},
		textAlign: "center",
	},
	inputCodeError: {
		"& input": {
			borderColor: "#eb4d4b!important",
			"&:focus": {
				borderColor: "#43BD98!important",
			},
		},
	},
});

export default function SignIn() {
	const { user, setUser } = useContext(UserContext);
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [password, setPassword] = useState("");
	const [repeatPassword, setRepeatPassword] = useState("");
	const [passwordScore, setPasswordScore] = useState(0);
	const [code, setCode] = useState("");
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const classes = useStyles();
	const [showLogin, setShowLogin] = useState(
		false
		//window.location.search.includes("auth=signup") ? false : true
	);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [showUpdatePassword, setShowUpdatePassword] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [checkTerms, setCheckTerms] = useState(false);
	const [videoLoaded, setVideoLoaded] = useState(false);
	const alert = useAlert();
	const matches = useMediaQuery("(min-width:900px)");
	const smallest = useMediaQuery("(min-width:480px)");
	const { t } = useTranslation();

	const location = useLocation();

	useEffect(() => {
		ReactGA.send({
			hitType: "pageview",
			page: window.location.href,
			title: "",
		});
	}, [location]);

	const validateName = (name) => {
		return name.trim().length > 0;
	};
	const validateEmail = (email) => {
		return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
			email
		);
	};

	const validatePassword = () => {
		return passwordScore >= 2;
	};

	const validateRepeatPassword = (password, repeatPassword) => {
		return password === repeatPassword;
	};

	const validateCode = (code) => {
		return code && code.length === 6;
	};

	const handleSubmitLogin = async (event) => {
		event.preventDefault();
		try {
			setLoading(true);
			let res = await axios.post(API_URL + "loginAdmin", {
				email,
				password,
			});

			localStorage.setItem("token", res.data.token);
			setUser(res.data.user);
		} catch (error) {
			console.log(error);
			// TO DO: Show error
			if (error.response.data.message === "NOT_ACTIVATED") {
				localStorage.setItem("token", error.response.data.token);
				setUser(error.response.data.user);
			} else if (error.response.data.message === "Not enabled yet") {
				alert.show(t("login.notEnabledYet"), { type: "success" });
			} else {
				alert.show(t("errors.invalidCredentials"), { type: "error" });
			}
		}
		setLoading(false);
	};

	const handleSubmitRegister = async (event) => {
		event.preventDefault();

		let registerErrors = {};

		if (!validateName(name)) {
			registerErrors.name = t("errors.invalidName");
		}
		if (!validateEmail(email)) {
			registerErrors.email = t("errors.invalidEmail");
		}
		if (!validatePassword(password)) {
			registerErrors.password = t("errors.invalidPassword");
		}
		if (!validateRepeatPassword(password, repeatPassword)) {
			registerErrors.repeatPassword = t("errors.passwordsDontMatch");
		}

		if (!checkTerms) {
			registerErrors.terms = t("errors.acceptTerms");
		}

		setErrors(registerErrors);

		if (Object.keys(registerErrors).length === 0) {
			try {
				setLoading(true);
				let res = await axios.post(API_URL + "registerAdmin", {
					name,
					email,
					password,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
					language: navigator.language || navigator.userLanguage,
				});

				localStorage.setItem("token", res.data.token);
				setUser(res.data.user);
			} catch (error) {
				console.log(error);
				let errorMessage = t("errors.generalError");
				let type = "error";

				if (error.response.data.message === "EMAIL_ALREADY_EXISTS") {
					errorMessage = t("errors.emailUsed");
				} else if (error.response.data.message === "Not enabled yet") {
					errorMessage = t("login.notEnabledYet");
					type = "success";
				}
				alert.show(errorMessage, { type: type });
			}
			setLoading(false);
		}
	};

	const handleSubmitForgotPassword = async (event) => {
		if (event) {
			event.preventDefault();
		}

		try {
			setLoading(true);
			await axios.post(API_URL + "adminForgotPassword", {
				email: email,
			});
			alert.show(t("login.newPasswordEmailSent"), {
				type: "success",
			});
			setShowForgotPassword(false);
			setShowUpdatePassword(true);
			setPassword("");
			setRepeatPassword("");
		} catch (error) {
			console.log(error);
			let errorMessage = t("errors.generalError");
			alert.show(errorMessage, { type: "error" });
		}
		setLoading(false);
	};

	const handleSubmitUpdatePassword = async (event) => {
		event.preventDefault();

		let registerErrors = {};

		if (!validateEmail(email)) {
			registerErrors.email = t("errors.invalidEmail");
		}
		if (!validatePassword(password)) {
			registerErrors.password = t("errors.invalidPasswords");
		}
		if (!validateRepeatPassword(password, repeatPassword)) {
			registerErrors.repeatPassword = t("errors.passwordsDontMatch");
		}
		if (!validateCode(code)) {
			registerErrors.code = t("errors.invalidCode");
		}

		setErrors(registerErrors);

		console.log(registerErrors);

		if (Object.keys(registerErrors).length === 0) {
			try {
				setLoading(true);
				await axios.post(API_URL + "adminUpdatePassword", {
					email: email,
					password: password,
					code: code,
				});
				alert.show(t("login.passwordChanged"), {
					type: "success",
				});
				setShowUpdatePassword(false);
				setShowLogin(true);
				setPassword("");
				setRepeatPassword("");
			} catch (error) {
				console.log(error);
				let errorMessage = t("errors.generalError");
				if (error.response.data.message === "INVALID_CODE") {
					errorMessage = t("errors.invalidCode");
				}
				alert.show(errorMessage, { type: "error" });
			}
			setLoading(false);
		}
	};

	const activateAccount = async (code) => {
		try {
			setLoading(true);
			let res = await axios.get(API_URL + "code", {
				params: {
					code,
					email: user.email,
					isAdmin: true,
				},
			});

			try {
				let url = new URL(window.location.href);
				if (
					(url.searchParams.get("appType") ?? "").includes("Services") ||
					(url.searchParams.get("appType") ?? "").includes("Ecommerce") ||
					(url.searchParams.get("appType") ?? "").includes("Woocommerce")
				) {
					await axios.patch(
						API_URL + "adminUsers",
						{
							chosenAppType: new Date().toISOString(),
							appType: (url.searchParams.get("appType") ?? "").includes(
								"Services"
							)
								? "Services"
								: "Ecommerce",
							appSubtype: (url.searchParams.get("appType") ?? "").includes(
								"Services"
							)
								? (url.searchParams.get("appSubtype") ?? "") &&
								  ["Barber", "Salon", "Trainer", "Other"].find(
										(t) => t === url.searchParams.get("appSubtype")
								  )
									? url.searchParams.get("appSubtype")
									: undefined
								: (url.searchParams.get("appType") ?? "").includes(
										"Ecommerce"
								  ) ||
								  (url.searchParams.get("appType") ?? "").includes(
										"Woocommerce"
								  )
								? (url.searchParams.get("appSubtype") ?? "") &&
								  ["Ecommerce", "Restaurant", "Other"].find(
										(t) => t === url.searchParams.get("appSubtype")
								  )
									? url.searchParams.get("appSubtype")
									: undefined
								: undefined,
							isOnlyWeb: url.searchParams.get("isOnlyWeb") !== null,
						},
						{
							headers: { authorization: localStorage.getItem("token") },
						}
					);
					window.location.href = window.location.origin;
				} else {
					setLoading(false);
					setUser(res.data.userFound);
				}
			} catch (error) {
				console.log(error);
				setLoading(false);
			}
		} catch (error) {
			console.log(error);
			setLoading(false);
			// TO DO: Show error
			let errorMessage = t("errors.generalError");

			if (error?.response?.data?.message === "EMAIL_ALREADY_EXISTS") {
				errorMessage = t("errors.invalidCode");
			}
			alert.show(errorMessage, { type: "error" });
		}
	};

	const resendCode = async (codeType) => {
		try {
			setLoading(true);
			await axios.post(API_URL + "resendCode", {
				email: user.email,
				isAdmin: true,
				codeType: codeType,
			});
			alert.show(t("login.newCodeEmailSent") + user.email, {
				type: "info",
			});
		} catch (error) {
			console.log(error);
			// TO DO: Show error
			let errorMessage = t("errors.generalError");
			alert.show(errorMessage, { type: "error" });
		}
		setLoading(false);
	};

	return (
		<Grid container component="main" sx={{ height: "100vh" }}>
			<CssBaseline />
			<Grid
				item
				xs={false}
				sm={5}
				md={6}
				lg={7}
				sx={{
					padding: { xs: 6, sm: 8 },
					color: "#ffffff",
					fontFamily: "Montserrat",
					display: { xs: "none", sm: "flex" },
					marginTop: { xs: 0, sm: -16 },
					position: "relative",
					width: "100%",
				}}
				justifyContent="center"
				flexDirection="column"
			>
				<div
					style={{
						position: "absolute",
						top: 0,
						bottom: 0,
						left: 0,
						right: 0,
						zIndex: -1,
					}}
					className={classes.videoContainer}
				>
					<Fade in={videoLoaded} timeout={2000}>
						<video
							playsInline
							autoPlay
							muted
							loop
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								objectFit: "cover",
								objectPosition: "center",
							}}
							onLoadedData={() => {
								setVideoLoaded(true);
							}}
						>
							<source
								src={"https://applaby-images.s3.amazonaws.com/video.mp4"}
								type="video/mp4"
							></source>
						</video>
					</Fade>
				</div>
				<Grid item>
					<Typography
						variant="h2"
						sx={{
							fontFamily: "Montserrat",
							lineHeight: { xs: 1.4, sm: 1.7 },
							padding: "0px 16px",
							display: "inline-flex",
							flexWrap: "wrap",
							backgroundColor: "#43BD98",
							marginBottom: 1,
						}}
					>
						APPLABY&nbsp;
						<b style={{ fontFamily: "Montserrat", fontWeight: "bolder" }}>
							CREATOR
						</b>
					</Typography>
				</Grid>
				<Grid item>
					<Typography
						variant="body1"
						sx={{ fontFamily: "Montserrat", fontSize: { xs: 14, sm: 18 } }}
					>
						{t("login.subtitle")}
					</Typography>
				</Grid>
			</Grid>
			<Grid
				item
				xs={12}
				sm={7}
				md={6}
				lg={5}
				component={Paper}
				elevation={6}
				square
				sx={{
					display: { xs: "flex", sm: "block" },
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Box
					sx={{
						marginBottom: { xs: 0, sm: 8 },
						mx: 4,
						py: { xs: 2, sm: 8 },
						px: matches ? 8 : smallest ? 4 : 1,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						height: "100%",
						justifyContent: "center",
						marginTop: { xs: 0, sm: showLogin ? "-128px" : "24px" },
					}}
				>
					<img src={Logo} alt="logo" style={{ width: 140, marginBottom: 20 }} />
					{user && !user.activatedAt ? (
						<>
							<Typography
								variant="body1"
								sx={{
									fontFamily: "Montserrat",
									textAlign: "center",
									marginBottom: 2,
								}}
							>
								{t("login.activateAccount")}
							</Typography>
							<ReactCodeInput
								type="number"
								fields={6}
								className={classes.inputCode}
								inputStyle={{
									fontFamily: "Montserrat",
									fontSize: 20,
									textAlign: "center",
									margin: "4px",
									width: "40px",
									borderRadius: "4px",
									height: "40px",
									border: "1px solid lightgray",
								}}
								disabled={loading}
								onChange={(e) => {
									if (e.length === 6) {
										activateAccount(e);
									}
								}}
							/>
							{loading && <CircularProgress size={24} color="primary" />}
							<Link
								href="#"
								variant="body2"
								sx={{ color: "#43BD98", textDecoration: "none", marginTop: 4 }}
								className={classes.link}
								disabled={loading}
								onClick={() => {
									resendCode("verification");
								}}
							>
								{t("login.newCode")}
							</Link>

							<Link
								href="#"
								variant="body2"
								sx={{
									color: "#262626!important",
									textDecoration: "none",
									marginTop: 4,
								}}
								className={classes.link}
								disabled={loading}
								onClick={() => {
									localStorage.removeItem("token");
									window.location.reload();
								}}
							>
								{t("login.otherAccount")}
							</Link>
						</>
					) : (
						<>
							{showLogin && !showForgotPassword && !showUpdatePassword && (
								<Box
									className={classes.signIn}
									component="form"
									noValidate
									onSubmit={handleSubmitLogin}
									sx={{ mt: 1, maxWidth: "560px" }}
								>
									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="email-input"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("login.email")}
										</InputLabel>
										<TextField
											id="email-input"
											type="email"
											fullWidth
											variant="outlined"
											value={email}
											onChange={(event) => {
												setEmail(event.target.value);
											}}
											inputProps={{ maxlength: 64 }}
										/>
									</FormControl>

									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="password"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("login.password")}
										</InputLabel>
										<OutlinedInput
											fullWidth
											variant="outlined"
											id="password"
											autoComplete="current-password"
											value={password}
											onChange={(event) => {
												setPassword(event.target.value);
											}}
											inputProps={{ maxlength: 64 }}
											type={"password"}
										/>
									</FormControl>

									{/*<FormControlLabel
										control={<Checkbox value="remember" />}
										label="Remember me"
										/>*/}
									<Button
										type="submit"
										fullWidth
										variant="contained"
										sx={{ mt: 3, mb: 2, width: "100%" }}
										disabled={loading}
									>
										{loading ? (
											<CircularProgress size={12} />
										) : (
											t("login.signIn")
										)}
									</Button>

									<Grid container>
										<Grid item xs={12} sm={12} sx={{ textAlign: "center" }}>
											<Link
												href="#"
												variant="body2"
												sx={{ color: "#43BD98", textDecoration: "none" }}
												className={classes.link}
												onClick={() => {
													setErrors({});
													setShowForgotPassword(true);
													setPassword("");
													setRepeatPassword("");
												}}
											>
												{t("login.forgetPassword")}
											</Link>
										</Grid>
									</Grid>
									<Grid item xs={12} sm={12}>
										<Button
											fullWidth
											variant="outlined"
											sx={{ mt: 3, mb: 2, width: "100%" }}
											onClick={() => {
												setErrors({});
												/*alert.show(t("login.notEnabledYet"), {
														type: "success",
													});*/
												setShowLogin(false);
												setPassword("");
												setRepeatPassword("");
											}}
										>
											{t("login.signUp")}
										</Button>
									</Grid>
									<Copyright sx={{ mt: 5 }} />
									<Link
										href="#"
										variant="body2"
										sx={{
											color: "#43BD98",
											textDecoration: "none",
											position: "absolute",
											bottom: "5px",
											right: "20px",
										}}
										className={classes.link}
										onClick={() => {
											window.location.href =
												window.location.origin + "/account-deletion";
										}}
									>
										{t("login.accountDeletion")}
									</Link>
								</Box>
							)}

							{showForgotPassword && (
								<Box
									className={classes.signIn}
									component="form"
									noValidate
									onSubmit={handleSubmitForgotPassword}
									sx={{ mt: 1, width: "100%", maxWidth: "560px" }}
								>
									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="email-input"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("login.email")}
										</InputLabel>
										<TextField
											id="email-input"
											type="email"
											fullWidth
											variant="outlined"
											value={email}
											onChange={(event) => {
												setEmail(event.target.value);
											}}
											inputProps={{ maxlength: 64 }}
										/>
									</FormControl>
									<Button
										type="submit"
										fullWidth
										variant="contained"
										sx={{ mt: 3, mb: 2, width: "100%" }}
										disabled={loading}
									>
										{loading ? (
											<CircularProgress size={12} />
										) : (
											t("login.resetPassword")
										)}
									</Button>

									<Grid container>
										<Grid
											item
											xs={12}
											justifyContent="center"
											sx={{ display: "flex" }}
										>
											<Link
												href="#"
												variant="body2"
												sx={{ color: "#43BD98", textDecoration: "none" }}
												className={classes.link}
												onClick={() => {
													setErrors({});
													setShowForgotPassword(false);
													setShowUpdatePassword(false);
													setShowLogin(true);
													setPassword("");
													setRepeatPassword("");
												}}
											>
												{t("login.alreadyHaveSignIn")}
											</Link>
										</Grid>
									</Grid>
									<Copyright sx={{ mt: 5 }} />
									<Link
										href="#"
										variant="body2"
										sx={{
											color: "#43BD98",
											textDecoration: "none",
											position: "absolute",
											bottom: "5px",
											right: "20px",
										}}
										className={classes.link}
										onClick={() => {
											window.location.href =
												window.location.origin + "/account-deletion";
										}}
									>
										{t("login.accountDeletion")}
									</Link>
								</Box>
							)}

							{showUpdatePassword && (
								<Box
									className={classes.signIn}
									component="form"
									noValidate
									onSubmit={handleSubmitUpdatePassword}
									sx={{
										mt: 1,
										width: "100%",
										maxWidth: "560px",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
									}}
								>
									<Typography
										variant="body1"
										sx={{
											fontFamily: "Montserrat",
											textAlign: "center",
											marginBottom: 2,
										}}
									>
										{t("login.resetPasswordInfo")}
									</Typography>
									<ReactCodeInput
										type="number"
										fields={6}
										className={
											classes.inputCode +
											" " +
											(errors.code ? classes.inputCodeError : "")
										}
										inputStyle={{
											fontFamily: "Montserrat",
											fontSize: 20,
											textAlign: "center",
											margin: "4px",
											width: "40px",
											borderRadius: "4px",
											height: "40px",
											border: "1px solid lightgray",
										}}
										disabled={loading}
										onChange={(e) => {
											setErrors({ ...errors, code: null });
											setCode(e);
										}}
									/>
									{loading && <CircularProgress size={24} color="primary" />}
									<Link
										href="#"
										variant="body2"
										sx={{
											color: "#43BD98",
											textDecoration: "none",
											marginTop: 2,
											marginBottom: 4,
										}}
										className={classes.link}
										disabled={loading}
										onClick={() => {
											handleSubmitForgotPassword(false);
										}}
									>
										{t("login.newCode")}
									</Link>
									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="password"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("login.newPassword")}
										</InputLabel>
										<OutlinedInput
											fullWidth
											variant="outlined"
											id="password"
											autocomplete="one-time-code"
											autoComplete="one-time-code"
											value={password}
											onChange={(event) => {
												setPassword(event.target.value);
											}}
											className={errors.password ? classes.errorInput : ""}
											type={showPassword ? "text" : "password"}
											endAdornment={
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => {
															setShowPassword(!showPassword);
														}}
														onMouseDown={(event) => {
															event.preventDefault();
														}}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											}
											inputProps={{ maxlength: 64 }}
										/>
										<PasswordStrengthBar
											password={password}
											className={classes.passwordStrengthBar}
											onChangeScore={(score, feedback) => {
												setPasswordScore(score);
												setErrors({
													...errors,
													password:
														password && score < 2
															? "Invalid password"
															: undefined,
												});
											}}
											scoreWords={[
												t("auth.passwordNotSecure"),
												t("auth.passwordWeak"),
												t("auth.passwordFair"),
												t("auth.passwordStrong"),
												t("auth.passwordVeryStrong"),
											]}
											shortScoreWord={
												password ? t("auth.passwordTooShort") : ""
											}
										/>
									</FormControl>

									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="repeatPassword"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("login.repeatPassword")}
										</InputLabel>
										<OutlinedInput
											fullWidth
											variant="outlined"
											id="repeatPassword"
											autocomplete="one-time-code"
											autoComplete="one-time-code"
											value={repeatPassword}
											onChange={(event) => {
												setRepeatPassword(event.target.value);
												if (
													!validateRepeatPassword(password, event.target.value)
												) {
													setErrors({
														...errors,
														repeatPassword: "Passwords don't match",
													});
												} else {
													setErrors({ ...errors, repeatPassword: undefined });
												}
											}}
											className={
												errors.repeatPassword ? classes.errorInput : ""
											}
											type={showPassword ? "text" : "password"}
											endAdornment={
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => {
															setShowPassword(!showPassword);
														}}
														onMouseDown={(event) => {
															event.preventDefault();
														}}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											}
											inputProps={{ maxlength: 64 }}
										/>
									</FormControl>

									<Button
										type="submit"
										fullWidth
										variant="contained"
										sx={{ mt: 3, mb: 2, width: "100%" }}
										disabled={loading}
									>
										{loading ? (
											<CircularProgress size={12} />
										) : (
											t("login.resetPassword")
										)}
									</Button>

									<Grid container>
										<Grid
											item
											xs={12}
											justifyContent="center"
											sx={{ display: "flex" }}
										>
											<Link
												href="#"
												variant="body2"
												sx={{ color: "#43BD98", textDecoration: "none" }}
												className={classes.link}
												onClick={() => {
													setErrors({});
													setShowForgotPassword(false);
													setShowUpdatePassword(false);
													setShowLogin(true);
													setPassword("");
													setRepeatPassword("");
												}}
											>
												{t("login.alreadyHaveSignIn")}
											</Link>
										</Grid>
									</Grid>
									<Copyright sx={{ mt: 5 }} />
									<Link
										href="#"
										variant="body2"
										sx={{
											color: "#43BD98",
											textDecoration: "none",
											position: "absolute",
											bottom: "5px",
											right: "20px",
										}}
										className={classes.link}
										onClick={() => {
											window.location.href =
												window.location.origin + "/account-deletion";
										}}
									>
										{t("login.accountDeletion")}
									</Link>
								</Box>
							)}

							{!showLogin && !showForgotPassword && (
								<Box
									className={classes.signUp}
									component="form"
									noValidate
									onSubmit={handleSubmitRegister}
									sx={{ mt: 1, maxWidth: "560px" }}
								>
									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="name-input"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("register.name")}
										</InputLabel>
										<TextField
											id="name-input"
											type="name"
											fullWidth
											variant="outlined"
											value={name}
											onChange={(event) => {
												setName(event.target.value);
												if (!validateName(event.target.value)) {
													setErrors({ ...errors, name: "Invalid name" });
												} else {
													setErrors({ ...errors, name: undefined });
												}
											}}
											className={errors.name ? classes.errorInput : ""}
											inputProps={{ maxlength: 64 }}
										/>
									</FormControl>
									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="email-input"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("register.email")}
										</InputLabel>
										<TextField
											id="email-input"
											type="email"
											fullWidth
											variant="outlined"
											value={email}
											onChange={(event) => {
												setEmail(event.target.value);
												if (!validateEmail(event.target.value)) {
													setErrors({ ...errors, email: "Invalid email" });
												} else {
													setErrors({ ...errors, email: undefined });
												}
											}}
											className={errors.email ? classes.errorInput : ""}
											inputProps={{ maxlength: 64 }}
										/>
									</FormControl>
									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="password"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("register.password")}
										</InputLabel>
										<OutlinedInput
											fullWidth
											variant="outlined"
											id="password"
											autocomplete="one-time-code"
											autoComplete="one-time-code"
											value={password}
											onChange={(event) => {
												setPassword(event.target.value);
											}}
											className={errors.password ? classes.errorInput : ""}
											type={showPassword ? "text" : "password"}
											endAdornment={
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => {
															setShowPassword(!showPassword);
														}}
														onMouseDown={(event) => {
															event.preventDefault();
														}}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											}
											inputProps={{ maxlength: 64 }}
										/>
										<PasswordStrengthBar
											password={password}
											className={classes.passwordStrengthBar}
											onChangeScore={(score, feedback) => {
												setPasswordScore(score);
												setErrors({
													...errors,
													password:
														password && score < 2
															? "Invalid password"
															: undefined,
												});
											}}
											scoreWords={[
												t("auth.passwordNotSecure"),
												t("auth.passwordWeak"),
												t("auth.passwordFair"),
												t("auth.passwordStrong"),
												t("auth.passwordVeryStrong"),
											]}
											shortScoreWord={
												password ? t("auth.passwordTooShort") : ""
											}
										/>
									</FormControl>
									<FormControl
										fullWidth
										variant="standard"
										sx={{ marginTop: 2, marginBottom: 2 }}
									>
										<InputLabel
											shrink
											htmlFor="repeatPassword"
											sx={{
												marginTop: -2,
												fontWeight: 700,
												textTransform: "uppercase",
												fontSize: 12,
											}}
										>
											{t("register.repeatPassword")}
										</InputLabel>
										<OutlinedInput
											fullWidth
											variant="outlined"
											id="repeatPassword"
											autocomplete="one-time-code"
											autoComplete="one-time-code"
											value={repeatPassword}
											onChange={(event) => {
												setRepeatPassword(event.target.value);
												if (
													!validateRepeatPassword(password, event.target.value)
												) {
													setErrors({
														...errors,
														repeatPassword: "Passwords don't match",
													});
												} else {
													setErrors({ ...errors, repeatPassword: undefined });
												}
											}}
											className={
												errors.repeatPassword ? classes.errorInput : ""
											}
											type={showPassword ? "text" : "password"}
											endAdornment={
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => {
															setShowPassword(!showPassword);
														}}
														onMouseDown={(event) => {
															event.preventDefault();
														}}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											}
											inputProps={{ maxlength: 64 }}
										/>
									</FormControl>
									<FormControlLabel
										sx={{ marginTop: 2 }}
										control={
											<Checkbox
												value={checkTerms}
												onChange={() => {
													setCheckTerms(!checkTerms);
												}}
												sx={{
													color: !errors.terms ? "inherit" : "red",
													fontSize: "14px",
												}}
											/>
										}
										label={
											<Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
												{t("register.registerSubtitle")}
												<a
													href={
														i18n.language === "es" ||
														i18n.language.includes("es-") ||
														i18n.language.includes("-ES") ||
														i18n.language.includes("es_") ||
														i18n.language.includes("_ES") ||
														i18n.language === "ca" ||
														i18n.language === "eu" ||
														i18n.language === "gl"
															? "https://applaby.com/es/terminos-y-condiciones.html"
															: "https://applaby.com/terms-and-conditions.html"
													}
													target={"_blank"}
													rel="noreferrer"
												>
													{t("register.termsAndConditions")}
												</a>
												{t("register.registerSubtitle2")}
												<a
													href={
														i18n.language === "es" ||
														i18n.language.includes("es-") ||
														i18n.language.includes("-ES") ||
														i18n.language.includes("es_") ||
														i18n.language.includes("_ES") ||
														i18n.language === "ca" ||
														i18n.language === "eu" ||
														i18n.language === "gl"
															? "https://applaby.com/es/politica-de-privacidad.html"
															: "https://applaby.com/privacy-policy.html"
													}
													target={"_blank"}
													rel="noreferrer"
												>
													{t("register.privacity")}
												</a>
											</Box>
										}
									/>
									<Button
										type="submit"
										fullWidth
										variant="contained"
										sx={{ mt: 3, mb: 2, width: "100%" }}
										disabled={loading}
									>
										{loading ? (
											<CircularProgress size={12} />
										) : (
											t("register.signUp")
										)}
									</Button>
									{/*<Typography
										component="body2"
										variant="body"
										style={{ display: "block", textAlign: "center" }}>
										or
									</Typography>
									<Button
										type="submit"
										fullWidth
										variant="contained"
										sx={{
											mt: 2,
											mb: 2,
											width: "100%",
											backgroundColor: "#ffffff",
											border: "1px solid #43BD98",
											color: "#43BD98",
											"&:hover": {
												color: "#ffffff",
											},
										}}
										disabled={loading}>
										{loading ? (
											<CircularProgress size={12} />
										) : (
											"Sign Up with Google"
										)}
										</Button>*/}
									<Grid container>
										<Grid
											item
											xs={12}
											justifyContent="center"
											sx={{ display: "flex" }}
										>
											<Link
												href="#"
												variant="body2"
												sx={{ color: "#43BD98", textDecoration: "none" }}
												className={classes.link}
												onClick={() => {
													setErrors({});
													setShowUpdatePassword(false);
													setShowForgotPassword(false);
													setShowLogin(true);
													setPassword("");
													setRepeatPassword("");
												}}
											>
												{t("login.alreadyHaveSignIn")}
											</Link>
										</Grid>
									</Grid>
									<Copyright sx={{ mt: 5 }} />
									<Link
										href="#"
										variant="body2"
										sx={{
											color: "#43BD98",
											textDecoration: "none",
											position: "absolute",
											bottom: "5px",
											right: "20px",
										}}
										className={classes.link}
										onClick={() => {
											window.location.href =
												window.location.origin + "/account-deletion";
										}}
									>
										{t("login.accountDeletion")}
									</Link>
								</Box>
							)}
						</>
					)}
				</Box>
			</Grid>
		</Grid>
	);
}
